import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Users, Store, Package, CheckSquare, ShieldCheck,
  BarChart3, Bell, Settings, ChevronDown, ChevronRight,
  Layers, ClipboardList, TrendingUp
} from 'lucide-react';
import logoImg from '../../logo.png';

const nav = [
  { path: '/', label: 'Дашборд', icon: LayoutDashboard },
  {
    label: 'Сотрудники', icon: Users, children: [
      { path: '/employees', label: 'Список сотрудников' },
      { path: '/employees/actions', label: 'Журнал действий' },
    ]
  },
  {
    label: 'Магазины', icon: Store, children: [
      { path: '/stores', label: 'Список магазинов' },
    ]
  },
  {
    label: 'Товары', icon: Package, children: [
      { path: '/products', label: 'Каталог товаров' },
      { path: '/products/categories', label: 'Категории / Иерархия' },
      { path: '/products/expiry', label: 'Сроки годности (ОСГ)' },
    ]
  },
  {
    label: 'Задачи', icon: CheckSquare, children: [
      { path: '/tasks', label: 'Все задачи' },
      { path: '/tasks/create', label: 'Создать задачу' },
      { path: '/tasks/templates', label: 'Шаблоны задач' },
    ]
  },
  {
    label: 'ОСГ Проверки', icon: ShieldCheck, children: [
      { path: '/osg/checks', label: 'Журнал проверок' },
      { path: '/osg/critical', label: 'Критические сроки' },
    ]
  },
  {
    label: 'Выкладка', icon: Layers, children: [
      { path: '/display/reports', label: 'Журнал выкладок' },
      { path: '/display/photos', label: 'Фото-отчёты' },
    ]
  },
  {
    label: 'Инвентаризация', icon: ClipboardList, children: [
      { path: '/inventory', label: 'Проверки остатков' },
    ]
  },
  {
    label: 'Аналитика', icon: BarChart3, children: [
      { path: '/analytics', label: 'Сводка' },
      { path: '/analytics/employees', label: 'По сотрудникам' },
      { path: '/analytics/stores', label: 'По магазинам' },
      { path: '/analytics/categories', label: 'По категориям' },
    ]
  },
  { path: '/notifications', label: 'Уведомления', icon: Bell },
  {
    label: 'Настройки', icon: Settings, children: [
      { path: '/settings/roles', label: 'Роли и права' },
      { path: '/settings', label: 'Общие настройки' },
      { path: '/settings/audit', label: 'Журнал событий' },
    ]
  },
];

function NavItem({ item, depth = 0 }) {
  const location = useLocation();
  const isChildActive = item.children?.some(c => location.pathname === c.path || location.pathname.startsWith(c.path + '/'));
  const [open, setOpen] = useState(isChildActive);
  const Icon = item.icon;

  if (item.children) {
    return (
      <div>
        <button onClick={() => setOpen(o => !o)} style={{
          display: 'flex', alignItems: 'center', gap: 10, width: '100%',
          padding: '9px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
          fontFamily: 'inherit', fontSize: 13, fontWeight: 500,
          background: isChildActive ? 'rgba(74,108,247,0.08)' : 'transparent',
          color: isChildActive ? '#4A6CF7' : '#5A5F7A',
          transition: 'all 0.15s',
        }}
          onMouseEnter={e => { if (!isChildActive) e.currentTarget.style.background = 'rgba(0,0,0,0.04)'; }}
          onMouseLeave={e => { if (!isChildActive) e.currentTarget.style.background = 'transparent'; }}>
          {Icon && <Icon size={17} />}
          <span style={{ flex: 1, textAlign: 'left' }}>{item.label}</span>
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        {open && (
          <div style={{ marginLeft: 16, borderLeft: '2px solid #F0F0F0', paddingLeft: 8, marginTop: 2, marginBottom: 2 }}>
            {item.children.map(c => (
              <NavLink key={c.path} to={c.path} style={({ isActive }) => ({
                display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px',
                borderRadius: 6, fontSize: 12.5, fontWeight: isActive ? 600 : 400,
                color: isActive ? '#4A6CF7' : '#6B7280',
                background: isActive ? 'rgba(74,108,247,0.08)' : 'transparent',
                textDecoration: 'none', transition: 'all 0.12s',
                marginBottom: 1,
              })}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'currentColor', flexShrink: 0 }} />
                {c.label}
              </NavLink>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <NavLink to={item.path} style={({ isActive }) => ({
      display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px',
      borderRadius: 8, fontSize: 13, fontWeight: isActive ? 600 : 500,
      color: isActive ? '#4A6CF7' : '#5A5F7A',
      background: isActive ? 'rgba(74,108,247,0.12)' : 'transparent',
      textDecoration: 'none', transition: 'all 0.12s',
    })}>
      {Icon && <Icon size={17} />}
      {item.label}
    </NavLink>
  );
}

export default function Sidebar({ collapsed }) {
  return (
    <aside style={{
      width: collapsed ? 0 : 250, minWidth: collapsed ? 0 : 250,
      height: '100vh', background: '#fff', borderRight: '1px solid #F0F0F0',
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
      transition: 'width 0.25s ease, min-width 0.25s ease',
      position: 'sticky', top: 0,
    }}>
      {/* Logo */}
      <div style={{
        padding: '0 16px', height: 64, display: 'flex', alignItems: 'center',
        borderBottom: '1px solid #F0F0F0', flexShrink: 0,
      }}>
        <img
          src={logoImg}
          alt="OSG Logo"
          style={{ height: 36, maxWidth: 200, objectFit: 'contain' }}
        />
      </div>
      {/* Nav */}
      <nav style={{ flex: 1, overflowY: 'auto', padding: '10px 8px', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {nav.map((item, i) => (
          <NavItem key={i} item={item} />
        ))}
      </nav>
      {/* Footer */}
      <div style={{ padding: '12px 16px', borderTop: '1px solid #F0F0F0', fontSize: 11, color: '#9CA3AF', textAlign: 'center' }}>
        OSG CPA v1.0.0
      </div>
    </aside>
  );
}
